import { Modal, Form, Select } from 'antd';
import { useEffect } from 'react';
import TreeSelectOrManualInput from '../components/TreeSelectOrManualInput';
import { LoopFormat, type IVariableTreeNode } from '../utils';
import type { ILoopInsertValues } from '../types';

export interface IProps {
  open: boolean;
  treeData: IVariableTreeNode[];
  loopContext?: {
    arrayPath: string;
    itemName: string;
    fullPath?: string;
  } | null;
  onConfirm: (values: ILoopInsertValues) => void;
  onCancel: () => void;
}

// Strip a leading "@". / @. root marker so nesting comparisons work
// regardless of whether either side carries it.
const stripRootMarker = (path: string): string =>
  path.replace(/^"@"\.|^@\./, '');

const formatOptions = [
  { value: LoopFormat.Plain, label: 'Plain' },
  { value: LoopFormat.BulletList, label: 'Bullet List' },
  { value: LoopFormat.NumberedList, label: 'Numbered List' },
  { value: LoopFormat.Table, label: 'Table' },
  { value: LoopFormat.DefinitionList, label: 'Definition List' },
];

const LoopInsertModal: React.FC<IProps> = ({
  open,
  treeData,
  loopContext,
  onConfirm,
  onCancel,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ format: LoopFormat.Plain });
    }
  }, [open, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const arrayPath = values.arrayPath;
      const normalizedArrayPath = stripRootMarker(arrayPath);

      // If the picked array lives inside the current loop, insert it as a
      // relative (item-scoped) loop - same as before, just no longer
      // limited to exactly one level below the context.
      const contextPath = loopContext
        ? stripRootMarker(loopContext.fullPath || loopContext.arrayPath)
        : null;
      const isNested = Boolean(
        contextPath && normalizedArrayPath.startsWith(`${contextPath}.`)
      );

      onConfirm({
        // Nested: use the relative (item-scoped) path, no root marker.
        // Root-level: keep the picked path verbatim (e.g. "@.items").
        arrayPath: isNested
          ? normalizedArrayPath.substring(contextPath!.length + 1)
          : arrayPath,
        format: values.format,
        isRelative: isNested,
        parentItemName: isNested ? loopContext?.itemName : undefined,
      });

      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="Insert Loop"
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Insert"
      cancelText="Cancel"
      destroyOnHidden
      width={480}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ format: LoopFormat.Plain }}
      >
        <Form.Item
          label="Array"
          name="arrayPath"
          rules={[{ required: true, message: 'Please select an array' }]}
          tooltip="Pick any array from your schema, at any depth"
        >
          <TreeSelectOrManualInput
            treeData={treeData}
            placeholder="Search arrays..."
          />
        </Form.Item>

        <Form.Item
          label="Display Format"
          name="format"
          rules={[{ required: true, message: 'Please select a format' }]}
          tooltip="How the loop content should be displayed"
        >
          <Select
            placeholder="Select format"
            options={formatOptions}
            autoFocus={false}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default LoopInsertModal;
